/**
 * The assistant's fixed copy that the web owns (agent-1-web W1/W3).
 *
 * Two kinds, kept apart on purpose:
 *
 * - **The backend's words, copied.** `ROUTE_TITLE`; `assistantCopy.test.ts` reads
 *   the sibling backend and fails if a single character differs. (The line under a
 *   fixed-wording reply, `MARKER_BY_REASON`, was removed by the owner 2026-10-12.)
 * - **The web's own interface words**: route names for the context chip, the
 *   tier pill, the refusal notices. None of them says anything about a measure
 *   or a person (the web authors no science, agent-1-web §1); the replies, the
 *   cards, the precautions, the caveats and the tool-step labels all arrive from
 *   the server and are rendered verbatim.
 */
import type { ChatRoute } from './assistant'
import type { RefusalCode } from './assistantThread'
import { formatWait } from './format'

/**
 * Screen names for the context chip, the backend's `ROUTE_TITLE` (session titles
 * are built from the same words, "Report · 11 Oct").
 */
export const ROUTE_TITLE: Readonly<Record<ChatRoute, string>> = {
  home: 'Home',
  report: 'Report',
  state: 'State',
  record: 'Record',
  'check-in': 'Check-in',
  baseline: 'Baseline',
  history: 'History',
  recording: 'Recording',
  compare: 'Compare',
  experiment: 'Experiment',
  account: 'Account',
  assistant: 'Assistant',
}

/** The notice for a refusal before the stream. DRAFT: owner copy review (D3). */
export function refusalCopy(code: RefusalCode, seconds: number | null): string {
  switch (code) {
    case 'chat.turn_in_progress':
      return 'A reply in this conversation is still being written. Wait for it, then send again.'
    case 'chat.rate_limited':
      return seconds === null
        ? 'You have sent a lot of messages. Try again later.'
        : `You have sent a lot of messages. Try again in ${formatWait(seconds)}.`
    case 'chat.busy':
      return seconds === null
        ? 'The assistant is busy. Try again shortly.'
        : `The assistant is busy. Try again in ${formatWait(seconds)}.`
    case 'chat.paused':
      // No countdown: a pause is an administrator's decision, and its five-minute
      // `Retry-After` is when to look again, not a promise.
      return PAUSED_COPY
  }
}

/**
 * The paused notice (agent-1.1 S6): an administrator has switched the assistant
 * off for now. The spec's words. Conversations stay readable; only sending waits.
 */
export const PAUSED_COPY = 'The assistant is paused for a moment. Your conversations are still here.'


/**
 * The unavailable notice (the canvas's sixth state), shown once when the chat
 * routes are absent. Product wording, not a measure's: it promises only that
 * the rest of the app still works. DRAFT (D3).
 */
export const ABSENT_COPY =
  'The assistant is taking a break. Your readings and the rest of the app are not affected; every card on Report still explains itself.'

/**
 * The drawer's one-line disclosure (agent-1.md Appendix A, with C41's "journal
 * notes"). DRAFT: the wording is the owner's (O2).
 */
export const DISCLOSURE = 'Describes your readings and this app. It does not diagnose, and it never reads your journal notes. It can say what differed between readings, never that the activity caused it.'

/**
 * "How the assistant works": Appendix A's six blocks, with C41's privacy
 * sentence until Sprint 1.11 ships export and deletion. DRAFT (O2).
 */
export const HOW_IT_WORKS: readonly { head: string; body: string }[] = [
  {
    head: 'It can',
    body: 'Explain any screen, describe your latest reading and each measure in plain words, and help you get a cleaner recording.',
  },
  {
    head: "It won't",
    body: 'Say how you feel, diagnose, compare you with other people, or invent a cause for a change.',
  },
  {
    head: 'Where answers come from',
    body: 'Only your stored readings, the measure cards written by the science team, and the app guide. Each reply shows what it looked at.',
  },
  {
    head: 'Checked before you see it',
    body: "Every reply is checked for claims those sources don't support. If a check fails, fixed wording is shown and marked as such.",
  },
  {
    head: 'Privacy',
    body: 'The AI model runs on Amazon Bedrock in London. It never sees your name, your raw signal or your journal notes. Conversations are stored in London and you can delete any of them at any time. They will be included in your data export and deleted with your account when those arrive.',
  },
]

export const TRUST_LINE = 'BrainSpot is a wellness and self-awareness tool. It is not a medical device and does not give medical advice.'

/** The thread's own notices: what happened to a turn, never what a reading means. DRAFT (D3). */
export const NOTICE_COPY = {
  dropped: "The reply didn't arrive. It may still be on its way.",
  stillWriting: 'This reply is still being written. Try again in a moment.',
  error: "This reply couldn't be finished.",
  gone: 'This conversation was deleted. Your next message starts a new one.',
  failed: 'Something went wrong. Try sending again.',
  invalid: "That message can't be sent. Keep it under 1,000 characters.",
  pending: 'This reply is still being written.',
} as const
